import {
  CreateProductType,
  DrainSchema,
  Drain,
  Drain_DrainageSystem,
  Drain_Location,
  Product,
} from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { create } from '@bufbuild/protobuf';
import { TFunction } from 'i18next';
import { TopologyGraph } from '~/shared/models/system/TopologyGraph';
import { ReservoirModel } from '~/shared/models/system/ReservoirModel';
import { assertUnreachable } from '~/types/assert-type';
import { CreateProductModel } from '~/shared/models/system/CreateProductModel';
import { ResourceNameIdConverter } from '~/shared/models/ResourceNameIdConverter';

export type DrainParameterDetails = {
  title: string;
  value: string | number;
  unit?: string;
};

const DEFAULT_FLOW_RESTRICTION_SPEED = 5;

type ReservoirFormOption = {
  label: string;
  value: string;
  disabled: boolean;
};

export type DrainFormValues = {
  toReservoirName: string | undefined;
  overflowHeight: string;
  flowRestrictionSpeed?: string;
  enableFlowRestriction?: boolean;
  passiveDrainCount?: string;
  drainageSystem: string;
  drainLocation: string;
};

export class DrainModel {
  readonly id: string;

  readonly name: string;

  readonly fromReservoirId: string;

  readonly fromReservoirName: string;

  readonly toReservoirId: string | undefined;

  readonly toReservoirName: string | undefined;

  readonly productId: string | undefined;

  readonly overflowHeight: number;

  readonly flowRestrictionSpeed?: number;

  readonly pressurePipe: boolean;

  readonly drainageSystem: Drain_DrainageSystem;

  readonly drainLocation: Drain_Location;

  readonly passiveDrainCount?: number;

  readonly canChangePassiveDrainCount: boolean;

  private allowedCreateProductTypes: Set<CreateProductType>;

  private allowedDrainageSystems: Drain_DrainageSystem[];

  private allowedDrainLocations: Drain_Location[];

  constructor(drainResponse: Drain, product?: Product) {
    this.name = drainResponse.name;
    this.id = ResourceNameIdConverter.drainNameToId(drainResponse.name);
    this.fromReservoirId = ResourceNameIdConverter.reservoirNameToId(
      drainResponse.fromReservoirName,
    );
    this.fromReservoirName = drainResponse.fromReservoirName;
    this.toReservoirId = drainResponse.toReservoirName
      ? ResourceNameIdConverter.reservoirNameToId(drainResponse.toReservoirName)
      : undefined;
    this.toReservoirName = drainResponse.toReservoirName || 'sewer';
    this.productId = product
      ? ResourceNameIdConverter.productNameToId(product.name)
      : undefined;
    this.overflowHeight = drainResponse.overflowHeight;
    this.flowRestrictionSpeed = drainResponse.flowRestrictionSpeed;
    this.pressurePipe = drainResponse.pressurePipe;
    this.drainageSystem = drainResponse.drainageSystem;
    this.drainLocation = drainResponse.location;
    this.passiveDrainCount = drainResponse.passiveDrainCount;

    this.canChangePassiveDrainCount =
      drainResponse.configOptions?.allowPassiveDrainCountSet ?? false;

    this.allowedCreateProductTypes = new Set(
      drainResponse.configOptions?.allowedCreateProductTypes,
    );

    this.allowedDrainageSystems =
      drainResponse.configOptions?.allowedDrainageSystems || [];

    this.allowedDrainLocations =
      drainResponse.configOptions?.allowedDrainageLocations || [];
  }

  toFormValues(): DrainFormValues {
    return {
      toReservoirName: this.toReservoirName,
      overflowHeight: this.overflowHeight.toString(),
      flowRestrictionSpeed: (
        this.flowRestrictionSpeed ?? DEFAULT_FLOW_RESTRICTION_SPEED
      ).toString(),
      enableFlowRestriction: Boolean(this.flowRestrictionSpeed),
      drainageSystem: this.drainageSystem.toString(),
      drainLocation: this.drainLocation.toString(),
      passiveDrainCount: this.passiveDrainCount?.toString(),
    };
  }

  toApiRequest(formValues: DrainFormValues) {
    const flowRestrictionSpeed =
      formValues.enableFlowRestriction &&
      formValues.flowRestrictionSpeed !== undefined
        ? parseFloat(formValues.flowRestrictionSpeed)
        : undefined;

    return create(DrainSchema, {
      name: this.name,
      fromReservoirName: this.fromReservoirName,
      toReservoirName:
        formValues.toReservoirName === 'sewer'
          ? undefined
          : formValues.toReservoirName,
      overflowHeight: parseFloat(formValues.overflowHeight),
      flowRestrictionSpeed,
      pressurePipe: this.pressurePipe,
      drainageSystem: parseFloat(formValues.drainageSystem),
      location: parseFloat(formValues.drainLocation),
      passiveDrainCount: formValues.passiveDrainCount
        ? parseFloat(formValues.passiveDrainCount)
        : undefined,
    });
  }

  reservoirsToConnect(
    topologyGraph: TopologyGraph,
    reservoirs: ReservoirModel[],
    t: TFunction,
  ): ReservoirFormOption[] {
    const filteredReservoirs = reservoirs.filter(
      (reservoir) => reservoir.name !== this.fromReservoirName,
    );

    const resevoirOptions = filteredReservoirs.map((reservoir) => {
      if (this.pressurePipe) {
        return {
          label: reservoir.title,
          value: reservoir.name,
          disabled: false,
        };
      }

      const willCreateCycle = topologyGraph.willCreateCycle(
        this.fromReservoirId,
        reservoir.id,
      );

      const cycleWarning = willCreateCycle
        ? ` - ${t('systemDesign.drain.edit.fields.labels.willCreateCycle')}`
        : '';

      return {
        label: reservoir.title + cycleWarning,
        value: reservoir.name,
        disabled: willCreateCycle,
      };
    });

    resevoirOptions.push({
      label: t('systemDesign.drain.edit.fields.labels.sewer'),
      value: 'sewer',
      disabled: false,
    });

    return resevoirOptions;
  }

  getTypeForReservoir(reservoirId: string) {
    return reservoirId === this.fromReservoirId ? 'source' : 'target';
  }

  private static getLabelForDrainageSystem(
    drainageSystem: Drain_DrainageSystem,
    t: TFunction,
  ) {
    switch (drainageSystem) {
      case Drain_DrainageSystem.GRAVITATIONAL:
        return t('systemDesign.drain.edit.fields.drainageSystem.gravitational');
      case Drain_DrainageSystem.SIPHONIC_QUICKSTREAM:
        return t(
          'systemDesign.drain.edit.fields.drainageSystem.siphonicQuickStream',
        );
      case Drain_DrainageSystem.SIPHONIC_UNIVERSAL:
        return t(
          'systemDesign.drain.edit.fields.drainageSystem.siphonicUniversal',
        );
      case Drain_DrainageSystem.UNSPECIFIED:
        return t('systemDesign.drain.edit.fields.drainageSystem.unspecified');
      default:
        return assertUnreachable(drainageSystem);
    }
  }

  static getLabelForDrainLocation(location: Drain_Location, t: TFunction) {
    switch (location) {
      case Drain_Location.MID_DRAIN:
        return t('systemDesign.drain.edit.fields.drainLocation.midDrain');
      case Drain_Location.SIDE_DRAIN:
        return t('systemDesign.drain.edit.fields.drainLocation.sideDrain');
      case Drain_Location.UNSPECIFIED:
        return t('systemDesign.drain.edit.fields.drainLocation.unspecified');
      default:
        return assertUnreachable(location);
    }
  }

  createDrainParameterDetails(
    t: TFunction,
    showAdditionalData: boolean,
    showDrainageSystemDetails: boolean = true,
    fromReservoir?: ReservoirModel,
  ): DrainParameterDetails[] {
    const details: DrainParameterDetails[] = [];

    details.push({
      title: t('systemDesign.drain.edit.fields.labels.overflowHeight'),
      value: this.overflowHeight,
      unit: 'mm',
    });

    if (
      this.shouldShowPassiveDrainCount(fromReservoir) &&
      this.passiveDrainCount
    ) {
      details.push({
        title: t('systemDesign.drain.edit.fields.labels.passiveDrainCount'),
        value: this.passiveDrainCount,
      });
    }

    if (!showAdditionalData) {
      return details;
    }

    if (this.flowRestrictionSpeed) {
      details.push({
        title: t('systemDesign.drain.edit.fields.labels.flowRestrictionSpeed'),
        value: this.flowRestrictionSpeed,
        unit: 'mm/h',
      });
    }

    if (!this.pressurePipe && showDrainageSystemDetails) {
      details.push({
        title: t('systemDesign.drain.edit.fields.labels.drainageSystem'),
        value: DrainModel.getLabelForDrainageSystem(this.drainageSystem, t),
      });

      details.push({
        title: t('systemDesign.drain.edit.fields.labels.drainLocation'),
        value: DrainModel.getLabelForDrainLocation(this.drainLocation, t),
      });
    }

    return details;
  }

  shouldShowPassiveDrainCount(fromReservoir?: ReservoirModel): boolean {
    return (
      this.canChangePassiveDrainCount &&
      !(fromReservoir && ReservoirModel.isTankType(fromReservoir.type))
    );
  }

  shouldDisableDrainageOptions(fromReservoir?: ReservoirModel): boolean {
    return Boolean(
      fromReservoir && ReservoirModel.isTankType(fromReservoir.type),
    );
  }

  getProductCreationDetails(t: TFunction) {
    const productCreationDetails = [];

    if (this.allowedCreateProductTypes.has(CreateProductType.POLDER_VALVE)) {
      productCreationDetails.push(
        CreateProductModel.getCreateProductDetails(
          {
            type: CreateProductType.POLDER_VALVE,
            drainName: this.name,
            reservoirName: this.fromReservoirName,
          },
          t,
        ),
      );
    }

    if (this.allowedCreateProductTypes.has(CreateProductType.VALVE)) {
      productCreationDetails.push(
        CreateProductModel.getCreateProductDetails(
          {
            type: CreateProductType.VALVE,
            drainName: this.name,
            reservoirName: this.fromReservoirName,
          },
          t,
        ),
      );
    }

    if (this.allowedCreateProductTypes.has(CreateProductType.PUMP)) {
      productCreationDetails.push(
        CreateProductModel.getCreateProductDetails(
          {
            type: CreateProductType.PUMP,
            drainName: this.name,
            reservoirName: this.fromReservoirName,
          },
          t,
        ),
      );
    }

    return productCreationDetails;
  }

  getDrainageSystemOptions(t: TFunction) {
    const allowedDrainageSystems =
      this.drainageSystem === Drain_DrainageSystem.UNSPECIFIED
        ? [Drain_DrainageSystem.UNSPECIFIED, ...this.allowedDrainageSystems]
        : this.allowedDrainageSystems;

    return allowedDrainageSystems.map((value) => ({
      value,
      label: DrainModel.getLabelForDrainageSystem(value, t),
    }));
  }

  getDrainLocationOptions(t: TFunction) {
    const allowedDrainLocations =
      this.drainLocation === Drain_Location.UNSPECIFIED
        ? [Drain_Location.UNSPECIFIED, ...this.allowedDrainLocations]
        : this.allowedDrainLocations;

    return allowedDrainLocations.map((value) => ({
      value,
      label: DrainModel.getLabelForDrainLocation(value, t),
    }));
  }
}
